/* eslint-disable import/no-cycle */
/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
import React, { useRef } from 'react';
import { MdDragIndicator } from 'react-icons/md';
import { HiTrash } from 'react-icons/hi';
import { Box, Button, HStack, Stack } from '@chakra-ui/core';
import { useStoreActions } from '../../../models/hooks';
import { DroppableZone } from './DroppableZone';
import { DropTarget, FormFieldT, FormSectionT } from './types';
import { isFieldSchema, isSectionSchema, removeFollowupOptionPath } from './utils';
import theme from '../../../styles/customTheme';
import DeleteFormElement from './actions/DeleteFormElement';
import useSectionDragAndDrop from './hooks/useSectionDragAndDrop';
import { SectionNameInput } from './formInputs/SectionNameInput';
import { FormField } from './FormField';

interface FormSectionProps {
  section: FormSectionT;
  path: number[];
  onDropItem: (dropTarget: DropTarget) => void;
  parentIsAdvanced?: boolean;
  optionId?: string;
  followupPath?: number[];
}

const FormSectionComponent: React.FC<FormSectionProps> = React.memo(
  ({ section, path, onDropItem, parentIsAdvanced, optionId, followupPath }) => {
    const { removeItemByPath, cleanedValidationErrors } = useStoreActions((actions) => actions.formBuilder);

    const itemRef = useRef<HTMLDivElement>(null);
    const isAdvanced = parentIsAdvanced || section.isAdvanced;

    const { handleDragStart, handleDragEnd, handleItemDrop, handleItemDragOver, handleDropInSection } = useSectionDragAndDrop({
      itemRef,
      path,
      section,
      isAdvanced,
      optionId,
      onDropItem,
    });

    const fields = section.fields?.filter((item) => (isFieldSchema(item) ? !item?.disabled : true));

    return (
      <Box
        ref={itemRef}
        onDragOver={handleItemDragOver}
        onDrop={handleItemDrop}
        id={section.id}
        data-id={section.id}
        data-path={path.join(',')}
      >
        <HStack sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <SectionNameInput sectionId={section.id} isAdvanced={isAdvanced} />
          {!parentIsAdvanced && (
            <HStack className="hide-in-drag-preview">
              <DeleteFormElement
                popoverTrigger={
                  <Button variant="ghost" colorScheme="red" style={{ aspectRatio: '1/1' }} padding={0.2} size="xs">
                    <HiTrash size={14} />
                  </Button>
                }
                onYes={() => {
                  let originalPath = [...path];
                  if (followupPath) {
                    originalPath = removeFollowupOptionPath(followupPath, path);
                  }
                  removeItemByPath({ path: originalPath });
                  cleanedValidationErrors();
                }}
              />
              <Button
                as="span"
                variant="ghost"
                cursor="grab"
                draggable={!parentIsAdvanced}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                data-testid={`drag-handle-section-${section.id}`}
                className="hide-in-drag-preview"
                style={{ aspectRatio: '1/1' }}
                padding={0.2}
                size="xs"
              >
                <MdDragIndicator size={14} color="gray.400" />
              </Button>
            </HStack>
          )}
        </HStack>
        <Box
          sx={{
            border: `2px solid ${isAdvanced ? '#62abe0' : theme.colors.gray[100]}`,
            borderRadius: 'md',
            backgroundColor: '#fff',
            cursor: isAdvanced ? 'not-allowed' : 'unset',
          }}
          className="hide-in-drag-preview"
        >
          <DroppableZone
            path={[...path, 0]}
            parentId={section.id}
            onDropItem={handleDropInSection}
            isAdvancedSectionTarget={isAdvanced}
            hideDropZoneMessage={section.fields.length > 0 || isAdvanced}
            className="section-internal-droppable"
            sx={{ paddingX: 2 }}
          />

          <Stack width="100%" spacing={0} className="hide-in-drag-preview">
            {fields.map((item, index) => (
              <React.Fragment key={item.id}>
                {isSectionSchema(item) ? (
                  <Box sx={{ paddingX: 4 }}>
                    <FormSection
                      section={item as FormSectionT}
                      path={[...path, index]}
                      onDropItem={handleDropInSection}
                      parentIsAdvanced={isAdvanced}
                      optionId={optionId}
                      followupPath={followupPath}
                    />
                  </Box>
                ) : (
                  <FormField
                    field={item as FormFieldT}
                    path={[...path, index]}
                    onDropItem={handleDropInSection}
                    parentIsAdvanced={isAdvanced}
                    optionId={optionId}
                    followupPath={followupPath}
                  />
                )}
                <DroppableZone
                  path={[...path, index + 1]}
                  parentId={section.id}
                  onDropItem={handleDropInSection}
                  isAdvancedSectionTarget={isAdvanced}
                  className="section-internal-droppable"
                  withDivider={index < fields.length - 1}
                  sx={{ paddingX: 2 }}
                />
              </React.Fragment>
            ))}
          </Stack>
        </Box>
      </Box>
    );
  },
);
export const FormSection = FormSectionComponent;

// export const FormSection = withViewportVisibility(FormSectionComponent, { rootMargin: '50px' });
